"use client";

import React from "react";
import ReactPhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./style.css";

/* ── Inline globe SVG ── */

function GlobeIcon({ active }: { active?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke={active ? "var(--ink)" : "var(--text-muted)"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" />
    </svg>
  );
}

/* ── Plain <input> adapter (matches antd outlined Input) ── */

const NativeInputAdapter = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>((props, ref) => (
  <input ref={ref} {...props} className="oe-phone-input__input" />
));
NativeInputAdapter.displayName = "NativeInputAdapter";

/* ── Public interface ── */

export interface PhoneInputProps {
  id?: string;
  name?: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  isInternational?: boolean;
  showInternationalToggle?: boolean;
  status?: "error" | "";
}

const PhoneInput = ({
  id,
  name,
  value,
  onChange,
  placeholder = "(555) 123-4567",
  disabled = false,
  isInternational = false,
  showInternationalToggle = false,
  status,
}: PhoneInputProps) => {
  const [localInternational, setLocalInternational] = React.useState(false);

  const effectiveInternational = showInternationalToggle
    ? localInternational
    : isInternational;

  const wrapperCls = [
    "oe-phone-input",
    effectiveInternational ? "" : "oe-phone-input--us-only",
    status === "error" ? "oe-phone-input--error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperCls} style={{ width: "100%" }}>
      <div style={{ position: "relative", width: "100%" }}>
        <ReactPhoneInput
          id={id}
          name={name}
          style={{ width: "100%" }}
          placeholder={placeholder}
          defaultCountry="US"
          value={value}
          international={false}
          onChange={(v) => onChange(v)}
          disabled={disabled}
          inputComponent={NativeInputAdapter}
        />
        {showInternationalToggle && (
          <button
            type="button"
            className={`oe-phone-input__globe${
              effectiveInternational ? " oe-phone-input__globe--active" : ""
            }`}
            onClick={() => setLocalInternational((prev) => !prev)}
            aria-label="Toggle international phone input"
            aria-pressed={effectiveInternational}
          >
            <GlobeIcon active={effectiveInternational} />
          </button>
        )}
      </div>
    </div>
  );
};

export default React.memo(PhoneInput);
